import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { API_XANO_CERTIFIED_BASE_URL } from '../config/api_config'
import { Api_url } from '../common/api_url'
import axios from 'axios'
import { axiosAuth } from '../config/axiosInstances'
import { gargiImage, shronitImage } from '../utilities/constants'


export const fetchNewMcquestions = createAsyncThunk(
    'newMcquestions',
    async ({ certified_user_skill_id }) => {
        const maxTries = 3
        let tries = 1
        while (tries < maxTries) {
            try {
                const response = await axios.post(API_XANO_CERTIFIED_BASE_URL + Api_url.GenerateMCQuestions, {
                    certified_user_skill_id
                })
                return response.data?.data
            } catch (error) {
                console.log(error.message)
                tries += 1
                if (tries === maxTries){
                    throw error
                }
            }
        }
    }
)

export const sendUserSkill = createAsyncThunk(
    'sendUserSkill',
    async ({ subject_name, utm_source, utm_medium, utm_campaign }) => {
        try {
            const response = await axios.post(API_XANO_CERTIFIED_BASE_URL + Api_url.SendUserSkill, {
                subject_name,
                utm_object: {
                    utm_source,
                    utm_medium,
                    utm_campaign
                }
            })
            return response.data?.data
        } catch (error) {
            console.log(error.message)
            throw error
        }
    }
)

export const saveUserQuizResponse = createAsyncThunk(
    'saveUserQuizResponse',
    async ({ certified_user_skill_quiz_id, quiz_attempt_object, quiz_completion_time_in_seconds, quiz_score }) => {
        try {
            const response = await axios.post(API_XANO_CERTIFIED_BASE_URL + Api_url.SaveUserResponse, {
                certified_user_skill_quiz_id, quiz_attempt_object, quiz_completion_time_in_seconds, quiz_score
            })

            return response.data?.data
        } catch (error) {
            throw error
        }
    }
)

export const dispatchUserGoal = createAsyncThunk(
    'dispatchUserGoal',
    async ({ certified_user_skill_id, user_goal }) => {
        try {
            const response = await axiosAuth.post(API_XANO_CERTIFIED_BASE_URL + Api_url.UpdateUserGoal, { certified_user_skill_id, user_goal })
            return response.data?.data
        } catch (error) {
            console.log(error)
            throw error
        }
    }
)

export const claimSkillCertificate = createAsyncThunk(
    'claimSkillCertificate',
    async ({ certified_user_skill_id }) => {
        try {
            const response = await axiosAuth.post(API_XANO_CERTIFIED_BASE_URL + Api_url.ClaimCertificate, {
                certified_user_skill_id
            })
            return response.data?.data
        } catch (error) {
            console.log(error.message)
        }
    }
)

export const fetchMCQAnalysis = createAsyncThunk(
    'mcqAnalysis/fetchMCQAnalysis',
    async ({ certified_user_skill_quiz_id }) => {
        const maxRetries = 2;
        let retries = 0;
        while (retries < maxRetries) {
            try {
                const response = await axiosAuth.get(API_XANO_CERTIFIED_BASE_URL + Api_url.UserQuizAnalysis, {
                    params: {
                        certified_user_skill_quiz_id
                    }
                })
                return response.data?.data

            } catch (e) {
                console.log(e.message)
                retries += 1;
                if (retries === maxRetries) throw e
            }
        }
    }
)

export const getUserFullDetails = createAsyncThunk(
    'getUserFullDetails',
    async ({ certified_user_skill_id }) => {
        try {
            const response = await axiosAuth.get(API_XANO_CERTIFIED_BASE_URL + Api_url.GetUserFullDetails, {
                params: {
                    certified_user_skill_id
                }
            })
            return response.data?.data
        } catch (error) {
            console.log(error.message)
        }
    }
)

const initialState = {
    mcqQuestions: {},
    typeformData: {},
    userResponse: {},
    questions: [],
    easyQuestionIndex: 0,
    mediumQuestionIndex: 0,
    hardQuestionIndex: 0,
    currentLevel: 'easy',
    currentStreak: 0,
    correctAnswersMap: {},
    textContext: [],
    showExplainThisModal: false,
    scenarioMap: {},
    shouldPlaySound: true,
    shouldPlaySecondAudio: false,
    naviagteTimerReset: null,
    anotherNavigateTimerReset: null,
    initialImage: '',
    currentImage: '',
    previousQuestionId: null
}

const mcqSlice = createSlice({
    name: 'mcquestions',
    initialState,
    reducers: {
        setQuestions: (state, action) => {
            state.questions.push(action.payload)
        },
        setQuizScore: (state, action) => {
            state.mcqQuestions = {
                ...state.mcqQuestions,
                quiz_score: action.payload
            }
        },
        setEasyQuestionIndex: (state) => {
            state.easyQuestionIndex += 1
        },
        setMediumQuestionIndex: (state) => {
            state.mediumQuestionIndex += 1
        },
        setHardQuestionIndex: (state) => {
            state.hardQuestionIndex += 1
        },
        setCurrentLevel: (state, action) => {
            state.currentLevel = action.payload
        },
        setCurrentStreak: (state, action) => {
            state.currentStreak = action.payload
        },
        setCorrectAnswer(state, action) {
            if(!state.correctAnswersMap){
                state.correctAnswersMap = {}
            }
            state.correctAnswersMap[action.payload.questionId] = action.payload.isCorrect
        },
        resetCorrectAnswers(state) {
            state.correctAnswersMap = {}
        },
        setTextContext: (state, action) => {
            if(state.textContext){
                state.textContext.push(action.payload)
            }else{
                state.textContext = [action.payload]
            }
        },
        setShowExplainThisModal: (state, action) => {
            state.showExplainThisModal = action.payload
        },
        resetAddedQuestions: (state) => {
            state.questions = []
            state.easyQuestionIndex = 0
            state.mediumQuestionIndex = 0
            state.hardQuestionIndex = 0
        },
        resetMCQuestions: (state) => {
            state.mcqQuestions = {}
        },
        resetQuestions: () => initialState,
        setPaidStatus: (state) => {
            state.typeformData = {
                ...state.typeformData,
                is_paid: true
            }
        },
        trackScenarioQuestion: (state, action) => {
            const { text_content, questionData } = action.payload;
            if (!text_content) return;

            if (!state.scenarioMap[text_content]) {
                state.scenarioMap[text_content] = [];
            }

            // Check if the question is already tracked for this scenario
            const alreadyExists = state.scenarioMap[text_content].some(q => q.q_id === questionData.q_id);
            if (!alreadyExists) {
                state.scenarioMap[text_content].push({
                    q_id: questionData.q_id,
                    question: questionData?.question,
                    answered: '',
                    questionNumber: questionData?.questionNumber // true or false
                });
            }
        },
        updateScenarioAnswerStatus: (state, action) => {
            const { text_content, q_id, answered } = action.payload
            if (!text_content || !state.scenarioMap[text_content]) return

            const question = state.scenarioMap[text_content].find(q => q.q_id === q_id)
            if (question) {
                question.answered = answered
            }
        },
        setScenarioMap: (state) => {
            state.scenarioMap = {}
        },
        setShouldPlaySound: (state) => {
            state.shouldPlaySound = !state.shouldPlaySound
        },
        setShouldPlaySecondAudio: (state, action) => {
            state.shouldPlaySecondAudio = action.payload
        },
        setNaviagteTimerReset: (state, action) => {
            state.naviagteTimerReset = action.payload
        },
        setAnotherNavigateTimerReset: (state, action) => {
            state.anotherNavigateTimerReset = action.payload
        },
        initializeImage: (state) => {
            if (!state.initialImage) {
                const randomImage = Math.random() < 0.5 ? shronitImage : gargiImage
                state.initialImage = randomImage
                state.currentImage = randomImage
            }
        },
        updateImageForQuestion: (state, action) => {
            const questionId = action.payload
            if (!state.initialImage) return
            if (questionId === 5) {
                state.currentImage = state.initialImage === shronitImage ? gargiImage : shronitImage
            } else if (questionId === 8) {
                state.currentImage = state.initialImage
            }
        },
        setPreviousQuestionId: (state, action) => {
            state.previousQuestionId = action.payload
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchNewMcquestions.fulfilled, (state, action) => {
            state.mcqQuestions = action.payload
        })
        builder.addCase(sendUserSkill.fulfilled, (state, action) => {
            state.typeformData = action.payload
        })
        builder.addCase(saveUserQuizResponse.fulfilled, (state, action) => {
            state.userResponse = action.payload
        })
        builder.addCase(dispatchUserGoal.fulfilled, (state, action) => {
            state.typeformData = action.payload
        })
        builder.addCase(claimSkillCertificate.fulfilled, (state, action) => {
            state.typeformData = action.payload
        })
        builder.addCase(fetchMCQAnalysis.fulfilled, (state, action) => {
            state.mcqQuestions = {
                ...state.mcqQuestions,
                quiz_analysis: action.payload
            }
        })
        builder.addCase(getUserFullDetails.fulfilled, (state, action) => {
            if (action.payload){
                const { beta_user_id, created_at, goal, id, is_paid, purchase_type, quiz_details, quiz_status,subject_name, updated_at } = action.payload
                state.typeformData = { beta_user_id, created_at, goal, id, is_paid, purchase_type, quiz_status, subject_name, updated_at }
                state.mcqQuestions = quiz_details
            }
        })
    }
})

export const { resetQuestions, setQuizScore, setPaidStatus, setQuestions, setEasyQuestionIndex, setMediumQuestionIndex, setHardQuestionIndex, setCurrentStreak, setCurrentLevel, resetMCQuestions, resetAddedQuestions, setTextContext, setShowExplainThisModal, trackScenarioQuestion, updateScenarioAnswerStatus, setScenarioMap, setShouldPlaySound, setShouldPlaySecondAudio, setNaviagteTimerReset, setAnotherNavigateTimerReset, setCorrectAnswer, initializeImage, updateImageForQuestion, setPreviousQuestionId } = mcqSlice.actions
export const selectMCQDetails = (state) => state.mcquestions.mcqQuestions
export const selectMCQ = (state) => state.mcquestions.mcqQuestions?.quiz_question_answer
export const selectTypeformData = (state) => state.mcquestions.typeformData
export const selectMcqAnalysis = (state) => state.mcquestions.mcqQuestions?.quiz_analysis
export const selectQuestions = (state) => state.mcquestions.questions
export const selectEasyQuestionIndex = (state) => state.mcquestions.easyQuestionIndex
export const selectMediumQuestionIndex = (state) => state.mcquestions.mediumQuestionIndex
export const selectHardQuestionIndex = (state) => state.mcquestions.hardQuestionIndex
export const selectCurrentLevel = (state) => state.mcquestions.currentLevel
export const selectCurrentStreak = (state) => state.mcquestions.currentStreak
export const selectTextContext = (state) => state.mcquestions.textContext
export const selectScenarioMap = (state) => state.mcquestions.scenarioMap
export const selectShowExplainThisModal = (state) => state.mcquestions.showExplainThisModal
export const selectShouldPlaySound = (state) => state.mcquestions.shouldPlaySound
export const selectShouldPlaySecondAudio = (state) => state.mcquestions.shouldPlaySecondAudio
export const selectNavigateTimerReset = (state) => state.mcquestions.naviagteTimerReset
export const selectAnotherNavigateTimerReset = (state) => state.mcquestions.anotherNavigateTimerReset
export const selectCorrectAnswersMap = (state) => state.mcquestions.correctAnswersMap
export const selectCurrentImage = (state) => state.mcquestions.currentImage
export const selectPreviousQuestionId = (state) => state.mcquestions.previousQuestionId
export default mcqSlice.reducer